import { useCareerSignals, CareerBadge } from "../../ai/CareerSignals";
import { aiHref } from "../../ai/useAiFeatures";
import React, { useMemo, useState } from "react";
import {
  TextField,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@material-ui/core";
import { fetchChildren, fetchUsers } from "../../api/user";
import { useReduxQuery } from "../../hooks/useReduxQuery";
import { playerStatusTypes, userTypes } from "../../utils/constants";
import SearchIcon from "@material-ui/icons/Search";
import DeleteIcon from "@material-ui/icons/Delete";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  useAgentDeletePlayer,
  useGetAgentPlayers,
  useGetPlayers,
} from "../../queries/usePlayers";
import { useDispatch, useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  tabs: {
    borderRadius: theme.shape.borderRadius,
    "& .MuiTabs-indicator": {
      backgroundColor: "#d32f2f",
      height: 3,
    },
  },
  tab: {
    minWidth: 0,
    fontWeight: 600,
    fontSize: "0.95rem",
    "&.Mui-selected": {
      color: "#d32f2f",
    },
    "&:hover": {
      color: "#d32f2f",
      opacity: 0.8,
    },
  },
}));

// R35: SAM lists an agent's client requests that wait for an administrator with pendingApproval and no
// contract or market data; they are shown, marked, and not opened.
export const PENDING_LABEL = "Pending approval";
export const isPendingClient = (player) => player?.pendingApproval === true;
// A pending request is withdrawn by its request id, a client by its player id.
export const deleteBody = (player) =>
  isPendingClient(player) ? { requestId: player.requestId } : { playerId: player.id };
// A pending request carries no account data (not even a name) until it is approved.
export const playerTitle = (player) =>
  isPendingClient(player) ? "Client request" : `${player?.firstName} ${player?.lastName}`;
// R36 (scan 6 review): the delete dialog names what is deleted, a pending request included.
export const deleteLabel = (player) =>
  isPendingClient(player) ? "this client request" : playerTitle(player);
// R36: the lists an agent, parent or spouse sees. A pending request has no account data, so neither its
// status nor its Pro/College type is known: it is listed with the clients (never the prospects) under
// every Pro/College tab, the same as under All.
export const listPlayers = ({ players, userType, selected, isProspects, agentPlayers, parent, filter }) => {
  let list = [...(players || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  if (selected) {
    if (userType === userTypes.PARENT || userType === userTypes.SPOUSE) {
      list = list.filter((player) => parent?.[player.id]);
    } else if (userType === userTypes.AGENT) {
      list = list
        // R35: the agent's own pending requests come from the same list (marked, by request id).
        .filter((player) => isPendingClient(player) || agentPlayers?.[player.id])
        .filter((player) =>
          isProspects
            ? !isPendingClient(player) && player.status === playerStatusTypes.PROSPECT
            : player.status !== playerStatusTypes.PROSPECT
        );
    }
  }
  // Apply Pro/College filter
  if (filter && filter !== "all") {
    list = list.filter((player) =>
      isPendingClient(player) ||
      (filter === "pro"
        ? player.userType === userTypes.PLAYER
        : player.userType === userTypes.COLLEGE)
    );
  }
  return list;
};
// R36: the player page reads the account from the cached allUsers list; a client approved after that list
// was loaded is fetched again before the page opens, instead of showing as not available until a reload.
export const needsUsersRefresh = (allUsers, player) =>
  !Array.isArray(allUsers) || !allUsers.some((u) => String(u.id) === String(player?.id));

const AllUsers = ({
  selected,
  fullWidth,
  isProspects,
  filter: initialFilter = "all",
  onFilterChange,
}) => {
  const history = useHistory();
  const aiSignals = useCareerSignals();
  const classes = useStyles();
  const [value, setValue] = useState("");
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const getPlayers = useGetPlayers();
  const currentUserType = useSelector((state) => state.user.data?.userType);
  const cachedUsers = useSelector((state) => state.user.data?.allUsers);
  const dispatch = useDispatch();
  const getAgentPlayers = useGetAgentPlayers({
    enabled: currentUserType === userTypes.AGENT,
  });
  const agentDeletePlayer = useAgentDeletePlayer({
    onSuccess: () => {
      handleCloseDeleteModal();
      getAgentPlayers.refetch();
      getPlayers.refetch();
    },
    onError: () => {
      alert("Failed to delete player. Please try again.");
      handleCloseDeleteModal();
    },
  });

  const handleFilterChange = (_, newValue) => {
    setActiveFilter(newValue);
    if (onFilterChange) {
      onFilterChange(newValue);
    }
  };

  // Always call both hooks regardless of the condition
  const parentQuery = useReduxQuery(
    currentUserType === userTypes.PARENT || currentUserType === userTypes.SPOUSE
      ? "Parent"
      : null,
    fetchChildren
  );

  // Destructure the results with default values if the hooks were not called
  const { parent = {} } = parentQuery;

  const allPlayers = useMemo(() => {
    return listPlayers({
      players: getPlayers?.data,
      userType: currentUserType,
      selected,
      isProspects,
      agentPlayers: getAgentPlayers?.data,
      parent,
      filter: activeFilter,
    });
  }, [
    getPlayers?.data,
    currentUserType,
    selected,
    parent,
    isProspects,
    getAgentPlayers?.data,
    activeFilter,
  ]);

  const filteredPlayers = useMemo(() => {
    return allPlayers.filter((player) =>
      playerTitle(player)
        .toLowerCase()
        .includes(value.toLowerCase())
    );
  }, [allPlayers, value]);

  const navigateToPlayer = (player) => {
    // R35: a pending request has no player page until an administrator approves it.
    if (isPendingClient(player)) return;
    const aiDestination = aiHref(`contracts?client_id=${encodeURIComponent(player.id)}`);
    if (selected && !isProspects && aiSignals.enabled && aiSignals.items.some(row => String(row.id) === String(player.id)) && aiDestination) { window.location.assign(aiDestination); return; }
    if (needsUsersRefresh(cachedUsers, player)) {
      Promise.resolve(dispatch(fetchUsers())).catch(() => {}).then(() => openPlayer(player));
      return;
    }
    openPlayer(player);
  };

  const openPlayer = (player) => {
    history.push({
      pathname: `${
        currentUserType === userTypes.AGENT ? "Agent/" : ""
      }Contract/${player.id}`,
      selectedUser: player,
      parent:
        currentUserType === userTypes.PARENT ||
        currentUserType === userTypes.SPOUSE,
    });
  };

  const handleDeleteClick = (e, player) => {
    e.stopPropagation();
    setPlayerToDelete(player);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setPlayerToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (playerToDelete?.id)
      agentDeletePlayer.mutate(deleteBody(playerToDelete));
  };

  const statusColors = {
    Active: { color: "green" },
    FreeAgent: { color: "blue" },
    InjuredReserve: { color: "red" },
    Retired: { color: "gray" },
    PUP: { color: "orange" },
    NFI: { color: "orange" },
    PracticeSquad: { color: "purple" },
  };

  return (
    <div style={{ width: "100%" }}>
      <Grid container>
        <Grid item xs={12}>
          <Tabs
            value={activeFilter}
            onChange={handleFilterChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            className={classes.tabs}
            style={{ marginBottom: "16px" }}
          >
            <Tab label="All" value="all" className={classes.tab} />
            <Tab label="Pro" value="pro" className={classes.tab} />
            <Tab label="College" value="college" className={classes.tab} />
          </Tabs>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {getPlayers?.isLoading ? (
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={30} color="inherit" />
            </div>
          </Grid>
        ) : (
          <>
            {allPlayers && allPlayers.length > 0 && (
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  placeholder="Search for a user..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {filteredPlayers && filteredPlayers.length > 0 ? (
              filteredPlayers.map((player, index) => (
                <Grid
                  item
                  {...(!fullWidth
                    ? { xs: 12 }
                    : { xs: 12, sm: 6, md: 4, lg: 4 })}
                  key={index}
                >
                  <Card
                    key={index}
                    onClick={() => navigateToPlayer(player)}
                    style={{ cursor: isPendingClient(player) ? "default" : "pointer" }}
                  >
                    <CardContent>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <h3>{playerTitle(player)}</h3>
                        {selected && !isProspects && <CareerBadge clientId={player.id} signals={aiSignals}/> }
                        {currentUserType === userTypes.AGENT && (
                          <DeleteIcon
                            style={{ color: "#ff1744", cursor: "pointer" }}
                            onClick={(e) => handleDeleteClick(e, player)}
                          />
                        )}
                      </div>

                      <p>
                        {player?.team
                          ? `${player?.team?.city} ${player?.team?.name}`
                          : "No Team"}
                      </p>
                      {isPendingClient(player) && (
                        <p role="status" style={{ color: "#b26a00", fontWeight: 600 }}>
                          {PENDING_LABEL}
                        </p>
                      )}
                      {player?.type !== "None" && <p>{player?.type}</p>}
                      <p
                        style={statusColors[player?.status?.replace(/\s/g, "")]}
                      >
                        {player?.status}
                      </p>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                {selected && allPlayers?.length === 0 && (
                  <p>
                    {currentUserType === userTypes.PARENT
                      ? "You currently do not have any players selected as children."
                      : currentUserType === userTypes.SPOUSE
                      ? "You currently do not have any players selected as a spouse."
                      : `You currently do not have any players selected as ${
                          isProspects ? "prospects" : "clients"
                        }.`}
                  </p>
                )}
                {(!selected || allPlayers?.length > 0) && (
                  <p>
                    There are currently no players in the database that match
                    your search criteria.
                  </p>
                )}
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {deleteLabel(playerToDelete)}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllUsers;
